import type {
  Application as ApplicationConfig,
  DefaultApplication as DefaultApplicationConfig,
  ChildApplication as ChildApplicationConfig,
  PathGroup,
} from '../../schema/types';
import type { ApplicationOverrideConfig } from '../../overrides';
import { validateAppPaths } from './validation';
import { generateAssetPrefixFromName } from './utils/generate-asset-prefix';
import { Host, LocalHost } from './host';
import { generateAutomationBypassEnvVarName } from './utils/generate-automation-bypass-env-var-name';

export class Application {
  readonly default: boolean;
  name: string;
  development: {
    local: LocalHost;
    fallback?: Host;
  };
  fallback?: Host;
  projectId?: string;
  packageName?: string;
  overrides?: {
    environment?: Host;
  };
  readonly serialized: ApplicationConfig;

  constructor(
    name: string,
    {
      app,
      overrides,
      isDefault,
    }: {
      app: ApplicationConfig;
      overrides?: ApplicationOverrideConfig;
      isDefault?: boolean;
    },
  ) {
    this.name = name;
    this.development = {
      local: new LocalHost({
        appName: name,
        localPort: app.development?.localPort,
        local: app.development?.local,
      }),
      fallback: app.development?.fallback
        ? new Host(app.development.fallback)
        : undefined,
    };
    if (app.development?.fallback) {
      this.fallback = new Host(app.development.fallback);
    }
    this.projectId = app.projectId;
    this.packageName = app.packageName;
    this.overrides = overrides?.environment
      ? {
          environment: new Host(overrides.environment),
        }
      : undefined;
    this.default = isDefault ?? false;
    this.serialized = app;
  }

  isDefault(): boolean {
    return this.default;
  }

  getAssetPrefix(): string {
    return generateAssetPrefixFromName({ name: this.name });
  }

  getAutomationBypassEnvVarName(): string {
    return generateAutomationBypassEnvVarName({ name: this.name });
  }

  serialize(): ApplicationConfig {
    return this.serialized;
  }
}

export class DefaultApplication extends Application {
  readonly default = true;
  fallback: Host;

  constructor(
    name: string,
    {
      app,
      overrides,
    }: {
      app: DefaultApplicationConfig;
      overrides?: ApplicationOverrideConfig;
    },
  ) {
    super(name, {
      app,
      overrides,
      isDefault: true,
    });

    this.fallback = new Host(app.development.fallback);
  }

  getAssetPrefix(): string {
    return '';
  }
}

export class ChildApplication extends Application {
  readonly default = false;
  routing: PathGroup[];

  constructor(
    name: string,
    {
      app,
      overrides,
    }: {
      app: ChildApplicationConfig;
      overrides?: ApplicationOverrideConfig;
    },
  ) {
    // validate
    ChildApplication.validate(name, app);

    super(name, {
      app,
      overrides,
      isDefault: false,
    });

    this.routing = app.routing;
  }

  static validate(name: string, app: ChildApplicationConfig): void {
    // validate routes
    validateAppPaths(name, app);
  }
}
